import type { User } from "@unbox/api";
import { Trash01 } from "@untitledui/icons";
import { useState } from "react";
import { DialogTrigger, Heading } from "react-aria-components";
import { useDeleteUser } from "@/api/admin/delete-user";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { CloseButton } from "@/components/base/buttons/close-button";
import { success } from "@/providers/ToastProvider";

type Props = { user: Pick<User, "id"> };

export const DeleteUserModal = ({ user }: Props) => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const { mutateAsync } = useDeleteUser();

  const onSubmit = async () => {
    await mutateAsync({ id: user.id });
    success("User deleted");

    close();
  };

  return (
    <DialogTrigger isOpen={open} onOpenChange={close}>
      <ButtonUtility size="xs" color="tertiary" tooltip="Delete" icon={Trash01} onClick={() => setOpen(true)} />
      <ModalOverlay isDismissable>
        <Modal className="dark-mode max-w-md">
          <Dialog>
            <div className="relative w-full overflow-hidden rounded-2xl bg-primary shadow-none transition-all sm:w-full">
              <CloseButton onClick={close} size="lg" className="absolute top-3 right-3" />
              <div className="flex flex-col gap-4 px-4 pt-5 mr-4 sm:px-6 sm:pt-6">
                <div className="z-10 flex flex-col gap-0.5">
                  <Heading slot="title" className="text-md font-semibold text-primary">
                    Delete user
                  </Heading>
                </div>
                <p className="">
                  Deleting this user will prevent them from logging in and mark them as archived. This email can be
                  invited again.
                </p>
              </div>
              <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                <Button color="secondary" size="lg" onClick={close}>
                  Cancel
                </Button>
                <Button color="primary-destructive" size="lg" type="button" onClick={onSubmit}>
                  Delete
                </Button>
              </div>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
};
